import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getPublicSongByToken } from "@/shared/api";
import { Loader } from "@/shared/ui";
import { IconArrowLeft } from "@tabler/icons-react";
import { Seo } from "@/shared/components/Seo";
import { useAuth } from "@/app/providers/AuthProvider";
import { usePlayer } from "@/app/providers/PlayerProvider";
import { useLanguage } from "@/app/providers/LanguageProvider";
import { useSelectLanguage } from "@/app/providers/useSelectLanguage";
import { Sidebar } from "@/widgets";
import { isTelegramMiniApp, isMobileDevice } from "@/shared/lib/telegram";
import { isMaxMiniApp } from "@/shared/lib/max";
import { SongHub } from "./SongHub";
import styles from "./SongPublicPage.module.scss";
/**
 * Публичная страница готовой песни по токену (`/song/:token`) — ХАБ всех медиа
 * песни (текст + минус + WAV + продление + видеообложка + караоке). Всё публично
 * (по токену). Тело хаба вынесено в переиспользуемый компонент `SongHub`
 * (variant="page"): он же используется как компактная панель деталей в «Моей
 * музыке» (variant="panel"). Здесь остаётся только загрузка данных по токену и
 * выбор layout-обёртки:
 *  - ВЛАДЕЛЕЦ залогинен на десктопе (owner_user_id === my user.id) → хаб встроен
 *    в САЙТ (сайдбар); гость/чужой/владелец-в-миниаппе → полноэкранный standalone.
 * Доп. поля payload (owner_user_id/audio_id/instrumental/wav/media_outputs)
 * опциональны — пока backend-ветка хаба не в проде, страница деградирует.
 */
export const SongPublicPage = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const [data, setData] = React.useState(null);
    const [status, setStatus] = React.useState("loading");
    const [sidebarOpen, setSidebarOpen] = React.useState(false);
    const { isAuthenticated, user } = useAuth();
    const { language } = useLanguage();
    const selectLanguage = useSelectLanguage();
    const { pause } = usePlayer();
    // На странице песни свой BarePlayer — глушим глобальный бар сразу на входе
    // (в т.ч. на экране «не найдено»), не дожидаясь загрузки данных.
    React.useEffect(() => {
        pause();
    }, [pause]);
    const t = React.useCallback((ru, en) => (language === "ru" ? ru : en), [language]);
    React.useEffect(() => {
        if (!token) {
            setStatus("error");
            return;
        }
        let cancelled = false;
        setStatus("loading");
        getPublicSongByToken(token)
            .then((song) => {
            if (cancelled)
                return;
            setData(song);
            setStatus("ok");
        })
            .catch(() => {
            if (!cancelled)
                setStatus("error");
        });
        return () => {
            cancelled = true;
        };
    }, [token]);
    if (status === "loading") {
        return (_jsx("div", { className: styles.centered, children: _jsx(Loader, { size: "lg" }) }));
    }
    if (status === "error" || !data || !token) {
        return (_jsxs("div", { className: styles.centered, children: [_jsx(Seo, { title: "\u041F\u0435\u0441\u043D\u044F \u043D\u0435 \u043D\u0430\u0439\u0434\u0435\u043D\u0430 \u2014 \u0412\u0438\u0440\u0442\u0443\u043E\u0437", noindex: true }), _jsxs("div", { className: styles.notFound, children: [_jsx("h1", { children: "\u041F\u0435\u0441\u043D\u044F \u043D\u0435 \u043D\u0430\u0439\u0434\u0435\u043D\u0430" }), _jsx("p", { children: "\u0421\u0441\u044B\u043B\u043A\u0430 \u043D\u0435\u0434\u0435\u0439\u0441\u0442\u0432\u0438\u0442\u0435\u043B\u044C\u043D\u0430 \u0438\u043B\u0438 \u043F\u0435\u0441\u043D\u044F \u0435\u0449\u0451 \u043D\u0435 \u0433\u043E\u0442\u043E\u0432\u0430." })] })] }));
    }
    const songTitle = `${data.title || "Песня"} — Виртуоз`;
    const isOwner = isAuthenticated &&
        data.owner_user_id != null &&
        user?.id === data.owner_user_id;
    // Сайдбар сайта имеет смысл только на десктопе (в мини-аппе/на мобилке у сайта
    // своя навигация). Мини-апп/узкий экран → отдаём standalone-хаб даже владельцу.
    const isMiniAppLike = isTelegramMiniApp() ||
        isMaxMiniApp() ||
        isMobileDevice() ||
        (typeof window !== "undefined" && window.innerWidth <= 770);
    const embedded = isOwner && !isMiniAppLike;
    // Тело хаба (переиспользуется в обоих layout'ах). Отличается лишь обёртка.
    const hubBody = (_jsxs("div", { className: styles.page, children: [_jsx(Seo, { title: songTitle, description: "\u041F\u0435\u0440\u0441\u043E\u043D\u0430\u043B\u044C\u043D\u0430\u044F \u043F\u0435\u0441\u043D\u044F. \u041E\u0442\u043A\u0440\u043E\u0439\u0442\u0435, \u0447\u0442\u043E\u0431\u044B \u043F\u043E\u0441\u043B\u0443\u0448\u0430\u0442\u044C \u0442\u0440\u0435\u043A \u0438 \u0441\u043A\u0430\u0447\u0430\u0442\u044C \u0435\u0433\u043E.", image: data.cover_url || undefined, type: "music.song", noindex: true }), _jsxs("div", { className: styles.shell, children: [isOwner && (_jsxs("button", { type: "button", className: styles.backBtn, onClick: () => navigate("/my-songs"), children: [_jsx(IconArrowLeft, { size: 16 }), _jsx("span", { children: t("Мои песни", "My songs") })] })), _jsx(SongHub, { token: token, data: data, variant: "page" })] })] }));
    // ВЛАДЕЛЕЦ (десктоп): хаб встроен в сайт — сайдбар.
    if (embedded) {
        return (_jsxs("div", { className: styles.ownerShell, children: [_jsx(Sidebar, { language: language, onSelectLanguage: selectLanguage, onOpenLoginModal: () => { }, onOpenRegisterModal: () => { }, isOpen: sidebarOpen, onToggle: () => setSidebarOpen((prev) => !prev) }), _jsx("div", { className: styles.ownerMain, children: hubBody })] }));
    }
    // ГОСТЬ/чужой (или владелец в мини-аппе): полноэкранный standalone-хаб.
    return _jsx("div", { className: "app app-share", children: hubBody });
};
export default SongPublicPage;
